import React, { useState, useEffect } from 'react';
import mammoth from 'mammoth';
import htmlSur from '../services/htmlSur';
import APIService from '../services/api';
import { cvsSources } from '../utils/cvDocument';

const BASE = 'https://besoins-tat-production.up.railway.app';

const BI = ({ icon, style, className = '' }) => <i className={`bi bi-${icon} ${className}`} style={style} />;
const Spin = ({ size = 14 }) => <BI icon="arrow-repeat" style={{ fontSize: size, animation: 'ats-spin .7s linear infinite', display: 'inline-block' }} />;
// Libellé de section — Space Mono uppercase, la grammaire des labels du shell.
const LBL = { fontFamily: 'var(--erp-font-mono)', fontSize: '10px', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)', marginBottom: '8px' };

// Nettoie les messages d'erreur pour l'utilisateur : pas de jargon « IA / API IA »,
// message clair quand un format de fichier n'a pas pu être lu.
const humanizeCvError = (msg) => {
  const m = String(msg || '');
  // Message backend déjà explicite (« Document illisible… format supporté : PDF, DOCX ») → tel quel.
  if (/illisible|format\s+support/i.test(m)) return m;
  // Ancien échec technique (base64/pdf) : message clair. PDF et DOCX sont supportés.
  if (/pdf.*(not valid|invalid)|base64|content\.\d+\.pdf|specified was not valid/i.test(m)) {
    return 'Ce fichier n\'a pas pu être lu (formats supportés : PDF, DOCX). Réessayez, ou utilisez un autre fichier.';
  }
  const cleaned = m
    .replace(/erreur\s+api\s+ia\s*:?/ig, '')
    .replace(/\bapi\s+ia\b/ig, 'analyse')
    .replace(/\bia\b/ig, 'analyse')
    .replace(/^[\s:–-]+/, '')
    .trim();
  return cleaned || 'Analyse du CV impossible. Réessayez dans un instant.';
};

export default function CVProcessorButton({ candidat, besoinId, showNotification, onUpdate }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button onClick={() => setShowModal(true)}
        className="bd-mini"
        title="Mettre le CV au format entreprise (modèle de votre société)">
        <BI icon="file-earmark-text" /> CV format entreprise
      </button>
      {showModal && <CVProcessorModal candidat={candidat} besoinId={besoinId} showNotification={showNotification} onClose={() => setShowModal(false)} onUpdate={onUpdate} />}
    </>
  );
}

export function CVProcessorModal({ candidat, besoinId, showNotification, onClose, onUpdate, existingGenCvs: existingGenCvsProp = [] }) {
  const [step, setStep] = useState('upload'); // upload | processing | result
  // Réduire ne coupe pas le traitement : le composant reste monté, seule la
  // modale se replie en pastille pour laisser travailler ailleurs.
  const [minimized, setMinimized] = useState(false);
  const [file, setFile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [cvData, setCvData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingDocx, setGeneratingDocx] = useState(false);
  const [preview, setPreview] = useState(null);      // HTML de l'aperçu du CV généré
  const [previewing, setPreviewing] = useState(false);
  const [fetchedGenCvs, setFetchedGenCvs] = useState(null); // CV entreprise existants lus sur la fiche canonique
  // LES CV DÉJÀ SUR LA FICHE (02.10 : « le candidat a un CV mais CV
  // entreprise ne le trouve pas »). Ouverte depuis le besoin, la modale ne
  // recevait aucune adresse de CV et demandait un fichier ; elle les lit
  // maintenant elle-même, dans la liste qu'elle chargeait déjà.
  const [sourcesFiche, setSourcesFiche] = useState(null); // null = lecture en cours
  const [sourceId, setSourceId] = useState(null);
  // Nom du fichier généré (contrat back #380) : facultatif, sans extension.
  // nomInitial = pré-remplissage ; pour un couple candidat×besoin, le nom est
  // mémorisé côté serveur → on ne renvoie nom_fichier QUE s'il a été modifié.
  const [nomFichier, setNomFichier] = useState('');
  const [nomInitial, setNomInitial] = useState('');

  const headers = { 'Authorization': `Bearer ${APIService.getToken()}` };
  const savesToFiche = candidat?.id != null;         // contexte fiche candidat → le CV est enregistré dessus
  const qui = `${candidat?.prenom || ''} ${candidat?.nom || ''}`.trim();

  // La modale détecte ELLE-MÊME les CV format entreprise déjà sur la fiche
  // (GET /candidates/:id/documents) — sinon, ouverte depuis la carte besoin
  // (sans prop existingGenCvs), « Ajouter » empilait un doublon au lieu de
  // proposer le remplacement. Mêmes critères que la fiche candidat.
  useEffect(() => {
    if (candidat?.id == null) { setFetchedGenCvs([]); setSourcesFiche([]); return; }
    let cancelled = false;
    fetch(`${BASE}/candidates/${candidat.id}/documents`, { headers })
      .then(r => r.ok ? r.json() : {})
      .then(j => {
        if (cancelled) return;
        const arr = j.documents || j.data?.documents || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
        const txt = (d) => `${d.type_piece || d.type || ''} ${d.label || ''} ${d.name || d.original_name || d.file_name || ''}`.toLowerCase();
        const gen = (Array.isArray(arr) ? arr : []).filter(d =>
          d.id != null && !/matrice/.test(txt(d))
          // Un CV rattaché à un besoin est un CV spécifique, pas un doublon du
          // générique : il ne compte ni pour l'avertissement ni pour le ménage.
          && d.besoin_id == null && d.besoinId == null && d.position_id == null
          && (d.source === 'generation' || /format[ _]?entreprise|cv[_ ]?tat/.test(txt(d))));
        setFetchedGenCvs(gen.map(d => ({ id: d.id, name: d.label || d.name || 'CV format entreprise' })));
        const sources = cvsSources(arr, { besoinId }).slice(0, 5);
        setSourcesFiche(sources);
        setSourceId(sources[0]?.id ?? null);
        // Pré-remplissage du nom de fichier : couple → le CV rattaché à CE
        // besoin s'il figure dans la liste ; fiche → le CV générique actuel.
        const courant = besoinId != null
          ? (Array.isArray(arr) ? arr : []).find(d => String(d.besoin_id ?? d.besoinId) === String(besoinId) && /cv/.test(txt(d)))
          : gen[0];
        const sansExt = String(courant?.original_name || courant?.file_name || '').replace(/\.[^.]+$/, '').trim();
        if (sansExt) { setNomFichier(sansExt); setNomInitial(sansExt); }
      })
      .catch(() => { if (!cancelled) { setFetchedGenCvs([]); setSourcesFiche([]); } });
    return () => { cancelled = true; };
  }, [candidat?.id, besoinId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prop (fiche candidat) prioritaire ; sinon détection maison (carte besoin & co)
  const existingGenCvs = existingGenCvsProp.length ? existingGenCvsProp : (fetchedGenCvs || []);
  // Une adresse reçue de l'écran (fiche candidat) prime ; sinon le CV de la
  // fiche choisi ici.
  const sourceFiche = !candidat?.cv_url && Array.isArray(sourcesFiche)
    ? (sourcesFiche.find(s => s.id === sourceId) || null) : null;
  const cvDispo = !!file || !!candidat?.cv_url || !!sourceFiche;

  useEffect(() => {
    fetch(`${BASE}/cv-processor/contacts`, { headers })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setContacts(list);
        if (list.length) setSelectedContact(list[0].key);
      }).catch(() => {});
  }, []);

  const processCV = async () => {
    setLoading(true);
    setStep('processing');
    try {
      let res;

      if (file) {
        // Fichier uploadé manuellement
        const formData = new FormData();
        formData.append('cv', file);
        if (selectedContact) formData.append('commercial_contact_name', selectedContact);
        res = await fetch(`${BASE}/cv-processor/process`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${APIService.getToken()}` },
          body: formData
        });
      } else if (candidat?.cv_url) {
        // Utiliser le CV déjà uploadé du candidat
        res = await fetch(`${BASE}/cv-processor/process-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
          body: JSON.stringify({ cv_url: candidat.cv_url, commercial_contact_name: selectedContact })
        });
      } else if (sourceFiche) {
        // Le CV choisi sur la fiche — par son adresse, comme depuis la fiche
        // candidat ; à défaut, le serveur va le chercher lui-même sur la fiche
        // (CV désigné du couple, sinon CV original).
        res = await fetch(`${BASE}/cv-processor/process-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
          body: JSON.stringify(sourceFiche.url
            ? { cv_url: sourceFiche.url, commercial_contact_name: selectedContact }
            : { candidate_id: candidat.id, besoin_id: besoinId ?? null, commercial_contact_name: selectedContact })
        });
      } else {
        showNotification('Aucun CV disponible', 'error');
        setStep('upload');
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Erreur traitement');

      setCvData(data.data);
      setStep('result');
      showNotification('CV analysé avec succès', 'success');
    } catch (e) {
      showNotification(humanizeCvError(e.message), 'error');
      setStep('upload');
    }
    setLoading(false);
  };

  // Génère le CV format entreprise.
  // - Contexte fiche/besoin : save_only=true → le BACKEND enregistre tout et répond en
  //   JSON (position doc du couple si besoin_id, sinon CV générique de la fiche), remplacé
  //   à chaque génération. Aucun binaire, aucun téléchargement, aucun upload côté front.
  // - Mode autonome (hors fiche) : pas de save_only → on récupère le .docx et on le télécharge.
  const generateCv = async () => {
    if (!cvData) return;
    setGeneratingDocx(true);
    try {
      const body = { data: cvData, candidat_nom: `${candidat?.prenom||''} ${candidat?.nom||''}`.trim().toUpperCase() };
      if (savesToFiche) {
        body.save_only = true;
        body.candidate_id = candidat.id;
        if (besoinId != null) body.besoin_id = besoinId;
      }
      // Nom du fichier (contrat #380) : couple → envoyé seulement si modifié
      // (mémorisé côté serveur, le renvoyer = le changer) ; fiche et
      // téléchargement → envoyé dès qu'il est renseigné (non mémorisé).
      const nomVoulu = nomFichier.trim();
      if (nomVoulu && (besoinId == null || nomVoulu !== nomInitial)) body.nom_fichier = nomVoulu;
      const res = await fetch(`${BASE}/cv-processor/generate-docx`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (savesToFiche) {
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
        const saved = j.saved || j.data?.saved || null;
        // Le label reste la clé interne « CV format entreprise » — le nom qui
        // suit le choix de l'utilisateur est original_name / filename.
        const nom = saved?.original_name || saved?.filename || j.filename || saved?.label || 'CV format entreprise';

        // Un seul CV générique sur la fiche : le message promet le remplacement,
        // on le garantit. La liste des existants date d'AVANT la génération —
        // tout ce qu'elle contient est un ancien CV, sauf réutilisation en place
        // par le serveur (même identifiant que saved). Les CV liés à un besoin
        // ne sont pas dans cette liste et restent intouchés.
        const savedId = saved?.id ?? saved?.document_id ?? null;
        if (besoinId == null && savedId != null && existingGenCvs.length > 0) {
          const anciens = existingGenCvs.filter(d => d.id != null && String(d.id) !== String(savedId));
          let restes = 0;
          for (const d of anciens) {
            try {
              const rd = await fetch(`${BASE}/candidates/${candidat.id}/documents/${d.id}`, { method: 'DELETE', headers });
              if (!rd.ok && rd.status !== 404) restes += 1;
            } catch (e) { restes += 1; }
          }
          if (restes > 0) showNotification(`${restes} ancien${restes > 1 ? 's' : ''} CV n'${restes > 1 ? 'ont' : 'a'} pas pu être retiré${restes > 1 ? 's' : ''} — supprimez-le${restes > 1 ? 's' : ''} depuis la fiche.`, 'error');
        }

        showNotification(besoinId != null ? `${nom} ajouté aux documents du besoin` : `${nom} enregistré — c'est le seul CV format entreprise de la fiche`, 'success');
        onUpdate?.();
        onClose?.();
      } else {
        if (!res.ok) throw new Error('Erreur génération');
        const blob = await res.blob();
        const filename = `${(nomVoulu || `CV_format_entreprise_${cvData.name || 'candidat'}`).replace(/\s+/g, '_')}.docx`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        showNotification('CV Word téléchargé', 'success');
      }
    } catch (e) { showNotification(humanizeCvError(e.message), 'error'); }
    finally { setGeneratingDocx(false); }
  };

  // Aperçu : génère le DOCX SANS candidat_id (rien n'est enregistré) et le rend en HTML via mammoth
  const previewDocx = async () => {
    if (!cvData) return;
    setPreviewing(true);
    try {
      const res = await fetch(`${BASE}/cv-processor/generate-docx`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: cvData, candidat_nom: `${candidat?.prenom||''} ${candidat?.nom||''}`.trim().toUpperCase(), preview: true })
      });
      if (!res.ok) throw new Error('Erreur génération');
      const blob = await res.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const out = await mammoth.convertToHtml({ arrayBuffer });
      setPreview(out.value || '<p><em>Aperçu vide</em></p>');
    } catch (e) {
      showNotification('Erreur aperçu : ' + e.message, 'error');
    }
    setPreviewing(false);
  };

  const busy = step === 'processing' || generatingDocx || previewing;

  // ── Réduit : pastille flottante, le traitement continue ──
  // Réduite, la pastille suit l'utilisateur d'un écran à l'autre : elle doit
  // donc dire DE QUI il s'agit — sinon, deux écrans plus loin, on ne sait
  // plus quel CV tourne.
  if (minimized) return (
    <button onClick={() => setMinimized(false)}
      title="Revenir au traitement du CV"
      style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 90, display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 14px', border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-md)', background: 'var(--erp-surface)', boxShadow: 'var(--erp-shadow-panel)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--erp-font-sans)' }}>
      <span style={{ width: '30px', height: '30px', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-accent-soft)', color: 'var(--erp-accent)' }}>
        {busy ? <Spin /> : <BI icon="file-earmark-text" />}
      </span>
      <span>
        <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>
          CV format entreprise{qui ? ` — ${qui}` : ''}
        </span>
        <span style={{ display: 'block', marginTop: '1px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
          {step === 'processing' ? 'Analyse en cours…' : generatingDocx ? 'Génération en cours…' : previewing ? 'Aperçu en cours…' : 'En attente — cliquez pour revenir'}
        </span>
      </span>
    </button>
  );

  return (
    <div className="erp-dialog-backdrop" style={{ zIndex: 90 }}>
      <div className="erp-dialog" role="dialog" aria-modal="true" aria-label="CV format entreprise"
        style={{ width: 'min(100%, 620px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* En-tête — même grammaire que les dialogues du shell */}
        <div className="erp-dialog__header" style={{ flex: 'none' }}>
          <span className="erp-dialog__icon"><BI icon="file-earmark-text" /></span>
          <div className="erp-dialog__heading">
            <h2>CV format entreprise</h2>
            <p>{qui ? `${qui} — mise au modèle de votre société` : 'Mise au modèle de votre société'}</p>
          </div>
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setMinimized(true)}
            title="Réduire — le traitement continue en arrière-plan">
            Réduire
          </button>
          <button type="button" className="erp-dialog__close" onClick={onClose} aria-label="Fermer"
            title={busy ? 'Fermer et abandonner le traitement' : 'Fermer'}>
            <BI icon="x-lg" />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px' }}>

          {/* UPLOAD */}
          {step === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Sélection contact commercial */}
              <div>
                <div style={LBL}>Contact commercial à associer</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {contacts.map(c => {
                    const sel = selectedContact === c.key;
                    return (
                      <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', borderRadius: 'var(--erp-radius-sm)', border: `1px solid ${sel ? 'var(--erp-accent)' : 'var(--erp-line)'}`, background: sel ? 'var(--erp-accent-soft)' : 'var(--erp-surface)', cursor: 'pointer', transition: 'border-color 120ms ease, background 120ms ease' }}>
                        <input type="radio" name="contact" value={c.key} checked={sel} onChange={() => setSelectedContact(c.key)}
                          style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0 }} />
                        <span className="erp-mono" style={{ width: '30px', height: '30px', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--erp-radius-xs)', fontSize: '13px', fontWeight: 700, background: sel ? 'var(--erp-accent)' : 'var(--erp-surface-muted)', color: sel ? 'var(--accent-fg)' : 'var(--erp-text-secondary)' }}>
                          {c.name[0]}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink)' }}>{c.name}</span>
                          <span style={{ display: 'block', marginTop: '1px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email} · {c.phone_display}</span>
                        </span>
                        {sel && <BI icon="check-lg" style={{ color: 'var(--erp-accent)', flex: 'none' }} />}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Upload fichier */}
              <div>
                <div style={LBL}>CV du candidat</div>

                {/* CV existant du candidat */}
                {candidat?.cv_url && !file && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', marginBottom: '8px', border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-surface-muted)' }}>
                    <BI icon="file-earmark-text" style={{ fontSize: '17px', color: 'var(--erp-text-tertiary)', flex: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{candidat.cv_original_name || 'CV existant'}</div>
                      <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>CV déjà sur la fiche — utilisé automatiquement</div>
                    </div>
                    <span className="erp-chip" style={{ flex: 'none' }}><BI icon="check-lg" /> Automatique</span>
                  </div>
                )}

                {/* Les CV de la fiche : celui qui sera mis au format est coché ;
                    s'il y en a plusieurs, on choisit d'un clic. */}
                {!candidat?.cv_url && !file && candidat?.id != null && sourcesFiche === null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', marginBottom: '8px', fontSize: '12.5px', color: 'var(--erp-text-tertiary)' }}>
                    <Spin size={13} /> Recherche du CV sur la fiche…
                  </div>
                )}
                {!candidat?.cv_url && !file && Array.isArray(sourcesFiche) && sourcesFiche.length > 0 && (
                  <div role="radiogroup" aria-label="CV de la fiche à mettre au format" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                    {sourcesFiche.map(s => {
                      const sel = s.id === sourceId;
                      const plusieurs = sourcesFiche.length > 1;
                      const quand = s.quand ? new Date(s.quand).toLocaleDateString('fr-CH') : null;
                      return (
                        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', border: `1px solid ${sel && plusieurs ? 'var(--erp-accent)' : 'var(--erp-line)'}`, borderRadius: 'var(--erp-radius-sm)', background: sel ? 'var(--erp-surface-muted)' : 'var(--erp-surface)', cursor: plusieurs ? 'pointer' : 'default', transition: 'border-color 120ms ease, background 120ms ease' }}>
                          <input type="radio" name="cv-source" checked={sel} onChange={() => setSourceId(s.id)}
                            style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0 }} />
                          <BI icon="file-earmark-text" style={{ fontSize: '17px', color: 'var(--erp-text-tertiary)', flex: 'none' }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.fichier || s.nom}>{s.nom}</div>
                            <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {[s.ceBesoin ? 'CV rattaché à ce besoin' : s.principal ? 'CV principal de la fiche' : 'CV de la fiche', quand ? `ajouté le ${quand}` : null].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          {sel && <span className="erp-chip" style={{ flex: 'none' }}><BI icon="check-lg" /> {plusieurs ? 'Choisi' : 'Utilisé'}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Upload manuel */}
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%', boxSizing: 'border-box', padding: '20px 16px', border: '1px dashed var(--erp-field-border)', borderRadius: 'var(--erp-radius-md)', background: file ? 'var(--erp-surface-muted)' : 'var(--erp-surface)', cursor: 'pointer', textAlign: 'center' }}>
                  {file ? (
                    <>
                      <BI icon="file-earmark-text" style={{ fontSize: '22px', color: 'var(--erp-text-tertiary)' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink)' }}>{file.name}</span>
                      <span style={{ fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
                        {(file.size / 1024).toFixed(0)} Ko · <span style={{ color: 'var(--erp-accent)', textDecoration: 'underline' }} onClick={e => { e.preventDefault(); setFile(null); }}>Retirer</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <BI icon="upload" style={{ fontSize: '20px', color: 'var(--erp-text-tertiary)' }} />
                      <span style={{ fontSize: '13px', color: 'var(--erp-text-secondary)' }}>{candidat?.cv_url || sourceFiche ? 'Ou choisissez un autre fichier (PDF, DOCX)' : 'Choisir le CV à traiter (PDF, DOCX)'}</span>
                    </>
                  )}
                  <input type="file" accept=".pdf,.docx" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px' }}>
                <button type="button" className="erp-btn erp-btn--secondary" onClick={onClose}>Annuler</button>
                <button type="button" className="erp-btn erp-btn--primary" onClick={processCV} disabled={!cvDispo || !selectedContact}
                  style={(!cvDispo || !selectedContact) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
                  <BI icon="file-earmark-text" /> Analyser le CV
                </button>
              </div>
            </div>
          )}

          {/* PROCESSING */}
          {step === 'processing' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '34px 0 26px', textAlign: 'center' }}>
              <span style={{ width: '52px', height: '52px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--erp-radius-md)', background: 'var(--erp-accent-soft)', color: 'var(--erp-accent)' }}>
                <Spin size={22} />
              </span>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>Traitement en cours…</p>
              <div style={{ fontSize: '12.5px', color: 'var(--erp-text-secondary)', lineHeight: 1.8 }}>
                Lecture du CV<br />Analyse du contenu<br />Mise en forme entreprise
              </div>
              <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setMinimized(true)}>
                Réduire et continuer à travailler
              </button>
            </div>
          )}

          {/* RESULT */}
          {step === 'result' && cvData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-md)', background: 'var(--erp-surface-muted)', padding: '13px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                  <span className="erp-mono" style={{ width: '38px', height: '38px', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--erp-radius-sm)', fontSize: '15px', fontWeight: 700, background: 'var(--erp-accent)', color: 'var(--accent-fg)' }}>
                    {(cvData.name || '?')[0]}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>{cvData.name}</p>
                    <p style={{ margin: '1px 0 0', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>{cvData.title}</p>
                  </div>
                </div>
                {cvData.profile && <p style={{ margin: '10px 0 0', fontSize: '12px', color: 'var(--erp-text-tertiary)', lineHeight: 1.5 }}>{cvData.profile.slice(0, 200)}…</p>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                {[
                  ['Compétences', `${(cvData.skills?.technical_skills?.length || 0) + (cvData.skills?.project_skills?.length || 0)} extraites`],
                  ['Expériences', `${cvData.experience?.length || 0} postes`],
                  ['Formation', `${cvData.education?.length || 0} diplômes`],
                  ['Contact', cvData.commercial_contact?.name?.split(' ')[0] || '—'],
                ].map(([l, v]) => (
                  <div key={l} style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', padding: '9px 11px' }}>
                    <div style={{ ...LBL, marginBottom: '5px', fontSize: '9.5px' }}>{l}</div>
                    <div className="erp-mono" style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Aperçu du CV généré (rendu via mammoth, rien n'est enregistré) */}
              {preview && (
                <div style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-md)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '7px 11px', background: 'var(--erp-surface-muted)', borderBottom: '1px solid var(--erp-line)' }}>
                    <span style={{ ...LBL, marginBottom: 0 }}><BI icon="eye" style={{ marginRight: '5px' }} />Aperçu — CV format entreprise</span>
                    <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setPreview(null)}>Fermer</button>
                  </div>
                  <div className="cvproc-preview" style={{ maxHeight: '288px', overflow: 'auto', padding: '14px', fontSize: '13px', color: 'var(--erp-ink)', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: htmlSur(preview) }} />
                </div>
              )}

              {/* Visualiser le CV avant de le confirmer */}
              <button type="button" className="erp-btn erp-btn--secondary" onClick={previewDocx} disabled={previewing || generatingDocx}
                style={{ width: '100%', ...(previewing || generatingDocx ? { opacity: 0.5 } : null) }}>
                {previewing ? <Spin /> : <BI icon="eye" />}
                {previewing ? 'Génération de l\'aperçu…' : preview ? 'Rafraîchir l\'aperçu' : 'Visualiser le CV'}
              </button>

              {/* Le backend remplace systématiquement à chaque génération : plus de choix
                  « Remplacer / Garder les deux ». Note d'info sur la fiche si un CV existe. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Nom du fichier généré — facultatif ; l'assainissement (extension,
                    caractères, préfixe CV_) est fait côté serveur. */}
                <div>
                  <div style={LBL}>Nom du fichier (facultatif)</div>
                  <input className="erp-field" value={nomFichier} onChange={e => setNomFichier(e.target.value)}
                    placeholder="Nom par défaut du serveur" style={{ width: '100%', boxSizing: 'border-box' }} />
                  <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--erp-text-tertiary)' }}>Sans extension — un préfixe « CV_ » est ajouté automatiquement s'il manque.</div>
                </div>
                {savesToFiche && besoinId == null && existingGenCvs.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', border: '1px solid var(--erp-warning)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '12px', lineHeight: 1.5 }}>
                    <BI icon="exclamation-triangle" style={{ flex: 'none', marginTop: '1px' }} />
                    <span>{existingGenCvs.length > 1 ? `${existingGenCvs.length} CV format entreprise existent sur la fiche — ils seront remplacés par celui-ci.` : 'Un CV format entreprise existe déjà sur la fiche — il sera remplacé.'} Les CV propres à un besoin ne sont pas touchés.</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px' }}>
                  <button type="button" className="erp-btn erp-btn--secondary" onClick={() => { setStep('upload'); setFile(null); setCvData(null); setPreview(null); }} disabled={generatingDocx}
                    style={generatingDocx ? { opacity: 0.5 } : undefined}>
                    Nouveau CV
                  </button>
                  <button type="button" className="erp-btn erp-btn--primary" onClick={generateCv} disabled={generatingDocx}
                    style={generatingDocx ? { opacity: 0.5 } : undefined}>
                    {generatingDocx ? <Spin /> : <BI icon={savesToFiche ? 'check-lg' : 'download'} />}
                    {besoinId != null ? 'Ajouter au besoin' : savesToFiche ? 'Ajouter à la fiche' : 'Télécharger Word'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
